import { c as _c } from "react/compiler-runtime";
import { IconChevronDownOutline18 } from "nucleo-ui-outline-18";
import { Input } from "@/components/ui/input";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchDiscordChannels, fetchDiscordGuilds, type DiscordChannel, type DiscordGuild } from "./discordChannels";
import { PixelLoader } from "@/components/ui/pixel-loader";
interface DiscordChannelSelectorProps {
  label: string;
  description?: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  connectorId?: string;
  connectorType?: string;
  disabled?: boolean;
}
export default function DiscordChannelSelector(t0) {
  const $ = _c(115);
  const {
    label,
    description,
    value,
    onChange,
    required,
    connectorId,
    connectorType,
    disabled: t1
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const resolvedConnectorType = connectorType || "pipedream:discord";
  const storageKey = connectorId ? `b3os:discord-guild:${connectorId}` : null;
  let t2;
  if ($[0] !== storageKey) {
    t2 = () => {
      if (storageKey && typeof window !== "undefined") {
        return localStorage.getItem(storageKey) || "";
      }
      return "";
    };
    $[0] = storageKey;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [selectedGuildId, setSelectedGuildId] = useState(t2);
  const [channelQuery, setChannelQuery] = useState("");
  const [isChannelOpen, setIsChannelOpen] = useState(false);
  const [isGuildOpen, setIsGuildOpen] = useState(false);
  const containerRef = useRef(null);
  let t3;
  let t4;
  if ($[2] !== connectorId || $[3] !== resolvedConnectorType) {
    t3 = ["discord-guilds", connectorId, resolvedConnectorType];
    t4 = () => fetchDiscordGuilds(connectorId, resolvedConnectorType);
    $[2] = connectorId;
    $[3] = resolvedConnectorType;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  const t5 = !!connectorId;
  let t6;
  if ($[6] !== t3 || $[7] !== t4 || $[8] !== t5) {
    t6 = {
      queryKey: t3,
      queryFn: t4,
      enabled: t5,
      staleTime: 300000,
      retry: 1
    };
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const {
    data: t7,
    isLoading: guildsLoading,
    isError: guildsError
  } = useQuery(t6);
  let t8;
  if ($[10] !== t7) {
    t8 = t7 === undefined ? [] : t7;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const guilds = t8;
  let t10;
  let t9;
  if ($[12] !== guilds || $[13] !== selectedGuildId || $[14] !== storageKey) {
    t9 = () => {
      if (guilds.length === 0) {
        return;
      }
      if (selectedGuildId && !guilds.some(g => g.id === selectedGuildId)) {
        const fallback = guilds.length === 1 ? guilds[0].id : "";
        setSelectedGuildId(fallback);
        if (storageKey) {
          if (fallback) {
            localStorage.setItem(storageKey, fallback);
          } else {
            localStorage.removeItem(storageKey);
          }
        }
        return;
      }
      if (guilds.length === 1 && !selectedGuildId) {
        setSelectedGuildId(guilds[0].id);
        if (storageKey) {
          localStorage.setItem(storageKey, guilds[0].id);
        }
      }
    };
    t10 = [guilds, selectedGuildId, storageKey];
    $[12] = guilds;
    $[13] = selectedGuildId;
    $[14] = storageKey;
    $[15] = t10;
    $[16] = t9;
  } else {
    t10 = $[15];
    t9 = $[16];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[17] !== connectorId || $[18] !== resolvedConnectorType || $[19] !== selectedGuildId) {
    t11 = ["discord-channels", connectorId, resolvedConnectorType, selectedGuildId];
    t12 = () => fetchDiscordChannels(connectorId, resolvedConnectorType, selectedGuildId);
    $[17] = connectorId;
    $[18] = resolvedConnectorType;
    $[19] = selectedGuildId;
    $[20] = t11;
    $[21] = t12;
  } else {
    t11 = $[20];
    t12 = $[21];
  }
  const t13 = !!connectorId && !!selectedGuildId;
  let t14;
  if ($[22] !== t11 || $[23] !== t12 || $[24] !== t13) {
    t14 = {
      queryKey: t11,
      queryFn: t12,
      enabled: t13,
      staleTime: 300000,
      retry: 1
    };
    $[22] = t11;
    $[23] = t12;
    $[24] = t13;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  const {
    data: t15,
    isLoading: channelsLoading,
    isError: channelsError
  } = useQuery(t14);
  let t16;
  if ($[26] !== t15) {
    t16 = t15 === undefined ? [] : t15;
    $[26] = t15;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  const channels = t16;
  let t17;
  let t18;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => {
      const handleClickOutside = e => {
        if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
          setIsChannelOpen(false);
          setIsGuildOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    };
    t18 = [];
    $[28] = t17;
    $[29] = t18;
  } else {
    t17 = $[28];
    t18 = $[29];
  }
  useEffect(t17, t18);
  let t19;
  bb0: {
    if (!channelQuery.trim()) {
      t19 = channels;
      break bb0;
    }
    let t20;
    if ($[30] !== channelQuery || $[31] !== channels) {
      const q = channelQuery.trim().replace(/^#/, "").toLowerCase();
      t20 = channels.filter(ch => ch.name.toLowerCase().includes(q));
      $[30] = channelQuery;
      $[31] = channels;
      $[32] = t20;
    } else {
      t20 = $[32];
    }
    t19 = t20;
  }
  const filteredChannels = t19;
  let t20;
  let t21;
  if ($[33] !== channels) {
    t21 = new Map(channels.map(_temp));
    $[33] = channels;
    $[34] = t21;
  } else {
    t21 = $[34];
  }
  t20 = t21;
  const channelsById = t20;
  let t22;
  let t23;
  if ($[35] !== guilds) {
    t23 = new Map(guilds.map(_temp2));
    $[35] = guilds;
    $[36] = t23;
  } else {
    t23 = $[36];
  }
  t22 = t23;
  const guildsById = t22;
  let t24;
  if ($[37] !== onChange) {
    t24 = channelId => {
      onChange(channelId);
      setIsChannelOpen(false);
      setChannelQuery("");
    };
    $[37] = onChange;
    $[38] = t24;
  } else {
    t24 = $[38];
  }
  const handleSelectChannel = t24;
  let t25;
  if ($[39] !== onChange || $[40] !== selectedGuildId || $[41] !== storageKey) {
    t25 = guildId => {
      if (guildId !== selectedGuildId) {
        setSelectedGuildId(guildId);
        if (storageKey) {
          localStorage.setItem(storageKey, guildId);
        }
        onChange("");
      }
      setIsGuildOpen(false);
    };
    $[39] = onChange;
    $[40] = selectedGuildId;
    $[41] = storageKey;
    $[42] = t25;
  } else {
    t25 = $[42];
  }
  const handleSelectGuild = t25;
  let t26;
  if ($[43] !== channelsById || $[44] !== value) {
    t26 = value ? channelsById.get(value) : undefined;
    $[43] = channelsById;
    $[44] = value;
    $[45] = t26;
  } else {
    t26 = $[45];
  }
  const selectedChannel = t26;
  let t27;
  if ($[46] !== guildsById || $[47] !== selectedGuildId) {
    t27 = selectedGuildId ? guildsById.get(selectedGuildId) : undefined;
    $[46] = guildsById;
    $[47] = selectedGuildId;
    $[48] = t27;
  } else {
    t27 = $[48];
  }
  const selectedGuild = t27;
  if (disabled) {
    let t28;
    if ($[49] !== label || $[50] !== required) {
      t28 = label && <label className="field-label block">{label}{required && <span className="ml-1 text-red-500">*</span>}</label>;
      $[49] = label;
      $[50] = required;
      $[51] = t28;
    } else {
      t28 = $[51];
    }
    let t29;
    if ($[52] !== selectedChannel || $[53] !== value) {
      t29 = selectedChannel ? `#${selectedChannel.name}` : value || <span className="text-ds-text-disabled">No channel selected</span>;
      $[52] = selectedChannel;
      $[53] = value;
      $[54] = t29;
    } else {
      t29 = $[54];
    }
    let t30;
    if ($[55] !== t29) {
      t30 = <div className="text-ds-text-secondary text-sm dark:text-zinc-200">{t29}</div>;
      $[55] = t29;
      $[56] = t30;
    } else {
      t30 = $[56];
    }
    let t31;
    if ($[57] !== t28 || $[58] !== t30) {
      t31 = <div className="space-y-1">{t28}{t30}</div>;
      $[57] = t28;
      $[58] = t30;
      $[59] = t31;
    } else {
      t31 = $[59];
    }
    return t31;
  }
  if (!connectorId) {
    let t28;
    if ($[60] !== label || $[61] !== required) {
      t28 = label && <label className="field-label block">{label}{required && <span className="ml-1 text-red-500">*</span>}</label>;
      $[60] = label;
      $[61] = required;
      $[62] = t28;
    } else {
      t28 = $[62];
    }
    let t29;
    if ($[63] !== description) {
      t29 = description && <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">{description}</p>;
      $[63] = description;
      $[64] = t29;
    } else {
      t29 = $[64];
    }
    let t30;
    if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
      t30 = <p className="text-ds-text-muted text-xs">Connect a Discord bot connector to select channels.</p>;
      $[65] = t30;
    } else {
      t30 = $[65];
    }
    let t31;
    if ($[66] !== t28 || $[67] !== t29) {
      t31 = <div className="space-y-2">{t28}{t29}{t30}</div>;
      $[66] = t28;
      $[67] = t29;
      $[68] = t31;
    } else {
      t31 = $[68];
    }
    return t31;
  }
  if (guildsLoading) {
    let t28;
    if ($[69] !== label || $[70] !== required) {
      t28 = label && <label className="field-label block">{label}{required && <span className="ml-1 text-red-500">*</span>}</label>;
      $[69] = label;
      $[70] = required;
      $[71] = t28;
    } else {
      t28 = $[71];
    }
    let t29;
    if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
      t29 = <div className="text-ds-text-disabled flex items-center gap-2 text-sm"><PixelLoader size="sm" />Loading servers...</div>;
      $[72] = t29;
    } else {
      t29 = $[72];
    }
    let t30;
    if ($[73] !== t28) {
      t30 = <div className="space-y-2">{t28}{t29}</div>;
      $[73] = t28;
      $[74] = t30;
    } else {
      t30 = $[74];
    }
    return t30;
  }
  if (guildsError) {
    let t28;
    if ($[75] !== label || $[76] !== required) {
      t28 = label && <label className="field-label block">{label}{required && <span className="ml-1 text-red-500">*</span>}</label>;
      $[75] = label;
      $[76] = required;
      $[77] = t28;
    } else {
      t28 = $[77];
    }
    let t29;
    if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
      t29 = <p className="text-ds-text-muted text-xs">Could not load servers. Make sure the Discord bot has been added to at least one server.</p>;
      $[78] = t29;
    } else {
      t29 = $[78];
    }
    let t30;
    if ($[79] !== t28) {
      t30 = <div className="space-y-2">{t28}{t29}</div>;
      $[79] = t28;
      $[80] = t30;
    } else {
      t30 = $[80];
    }
    return t30;
  }
  if (guilds.length === 0) {
    let t28;
    if ($[81] !== label || $[82] !== required) {
      t28 = label && <label className="field-label block">{label}{required && <span className="ml-1 text-red-500">*</span>}</label>;
      $[81] = label;
      $[82] = required;
      $[83] = t28;
    } else {
      t28 = $[83];
    }
    let t29;
    if ($[84] === Symbol.for("react.memo_cache_sentinel")) {
      t29 = <p className="text-ds-text-muted text-xs">The bot hasn't been added to any servers yet. Add the bot to a Discord server first.</p>;
      $[84] = t29;
    } else {
      t29 = $[84];
    }
    let t30;
    if ($[85] !== t28) {
      t30 = <div className="space-y-2">{t28}{t29}</div>;
      $[85] = t28;
      $[86] = t30;
    } else {
      t30 = $[86];
    }
    return t30;
  }
  let t28;
  if ($[87] !== label || $[88] !== required) {
    t28 = label && <label className="field-label block">{label}{required && <span className="ml-1 text-red-500">*</span>}</label>;
    $[87] = label;
    $[88] = required;
    $[89] = t28;
  } else {
    t28 = $[89];
  }
  let t29;
  if ($[90] !== description) {
    t29 = description && <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">{description}</p>;
    $[90] = description;
    $[91] = t29;
  } else {
    t29 = $[91];
  }
  let t30;
  if ($[92] !== guilds || $[93] !== handleSelectGuild || $[94] !== isGuildOpen || $[95] !== selectedGuild || $[96] !== selectedGuildId) {
    t30 = guilds.length > 1 && <div className="relative"><label className="field-label text-ds-text-muted mb-1 block text-xs">Server</label><button type="button" onClick={() => {
        setIsGuildOpen(!isGuildOpen);
        setIsChannelOpen(false);
      }} className="border-ds-neutral-300 text-ds-text-primary dark:border-ds-neutral-700 bg-ds-surface-card flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm hover:border-zinc-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:hover:border-zinc-600">{selectedGuild ? <span>{selectedGuild.name}</span> : <span className="text-ds-text-disabled dark:text-ds-text-muted">Select a server...</span>}<IconChevronDownOutline18 className="text-ds-text-disabled h-4 w-4" /></button>{isGuildOpen && <div className="border-ds-border-default bg-ds-surface-card shadow-ds-elev-2 absolute z-50 mt-1 max-h-48 w-full overflow-auto rounded-lg border">{guilds.map(g_1 => <button key={g_1.id} type="button" onClick={() => handleSelectGuild(g_1.id)} className={`hover:bg-ds-surface-hover flex w-full items-center gap-2 px-3 py-2 text-left text-sm ${g_1.id === selectedGuildId ? "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" : "text-ds-text-primary"}`}><span className="min-w-0 flex-1 truncate">{g_1.name}</span></button>)}</div>}</div>;
    $[92] = guilds;
    $[93] = handleSelectGuild;
    $[94] = isGuildOpen;
    $[95] = selectedGuild;
    $[96] = selectedGuildId;
    $[97] = t30;
  } else {
    t30 = $[97];
  }
  let t31;
  if ($[98] !== channelQuery || $[99] !== channelsError || $[100] !== channelsLoading || $[101] !== filteredChannels || $[102] !== guilds.length || $[103] !== handleSelectChannel || $[104] !== isChannelOpen || $[105] !== selectedChannel || $[106] !== selectedGuild?.name || $[107] !== selectedGuildId || $[108] !== value) {
    t31 = selectedGuildId && <div className="relative">{guilds.length === 1 && <p className="text-ds-text-muted mb-1 text-xs">Server: <span className="text-ds-text-secondary font-medium">{selectedGuild?.name}</span></p>}{channelsLoading ? <div className="text-ds-text-disabled flex items-center gap-2 text-sm"><PixelLoader size="sm" />Loading channels...</div> : channelsError ? <p className="text-ds-text-muted text-xs">Could not load channels for this server.</p> : isChannelOpen ? <><Input type="text" value={channelQuery} onChange={e_0 => setChannelQuery(e_0.target.value)} placeholder="Search channels..." autoFocus={true} className="text-ds-text-primary dark:bg-ds-neutral-800 border-blue-500 ring-2 ring-blue-500/20 dark:border-blue-500" /><div className="border-ds-border-default bg-ds-surface-card shadow-ds-elev-2 absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg border">{filteredChannels.length === 0 ? <div className="text-ds-text-disabled px-3 py-2 text-sm">{channelQuery ? "No channels match your search" : "No messageable channels found"}</div> : filteredChannels.map(ch_1 => <button key={ch_1.id} type="button" onClick={() => handleSelectChannel(ch_1.id)} className={`hover:bg-ds-surface-hover flex w-full items-center gap-2 px-3 py-2 text-left text-sm ${ch_1.id === value ? "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" : "text-ds-text-primary"}`}><span className="min-w-0 flex-1 truncate">#{ch_1.name}</span></button>)}</div></> : <button type="button" onClick={() => {
        setIsChannelOpen(true);
        setIsGuildOpen(false);
      }} className="border-ds-neutral-300 text-ds-text-primary dark:border-ds-neutral-700 bg-ds-surface-card flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm hover:border-zinc-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:hover:border-zinc-600">{selectedChannel ? <span>#{selectedChannel.name}</span> : <span className="text-ds-text-disabled dark:text-ds-text-muted">Select a channel...</span>}<IconChevronDownOutline18 className="text-ds-text-disabled h-4 w-4" /></button>}</div>;
    $[98] = channelQuery;
    $[99] = channelsError;
    $[100] = channelsLoading;
    $[101] = filteredChannels;
    $[102] = guilds.length;
    $[103] = handleSelectChannel;
    $[104] = isChannelOpen;
    $[105] = selectedChannel;
    $[106] = selectedGuild?.name;
    $[107] = selectedGuildId;
    $[108] = value;
    $[109] = t31;
  } else {
    t31 = $[109];
  }
  let t32;
  if ($[110] !== t28 || $[111] !== t29 || $[112] !== t30 || $[113] !== t31) {
    t32 = <div className="space-y-2" ref={containerRef}>{t28}{t29}{t30}{t31}</div>;
    $[110] = t28;
    $[111] = t29;
    $[112] = t30;
    $[113] = t31;
    $[114] = t32;
  } else {
    t32 = $[114];
  }
  return t32;
}
function _temp2(g_0) {
  return [g_0.id, g_0];
}
function _temp(ch_0) {
  return [ch_0.id, ch_0];
}